// SignalRContext.tsx
import React, { createContext, useEffect, useState } from 'react';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import useAuth from 'hooks/useAuth';
import { applyServerContext } from 'utils/axios';
import { useSnackbar } from 'notistack';
import { writePerformanceLog } from 'utils/logs';
import { dispatch } from 'store';
import { setChatUsers, setChatUsersRequested, setChatUsersSavedMessages } from 'store/slices/chat';
import { getDataOnLoad } from 'services/dataLoader';

interface SignalRContextType {
  connection: HubConnection | null;
}

const SignalRContext = createContext<SignalRContextType>({ connection: null });

const SignalRProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [connection, setConnection] = useState<HubConnection | null>(null);

  const isConnectionRef = React.useRef<boolean>(false);

  //* Socket connection
  useEffect(() => {
    if (!token) return;

    let connectionRetries = [0, 5000, 10000, 20000, 60000];
    // Create connection

    const newConnection = new HubConnectionBuilder()
      .withUrl(
        `${applyServerContext(import.meta.env.VITE_APP_SOCKET_API_URL)}/hub/signalr?access_token=${token}`,
      )
      .withAutomaticReconnect(connectionRetries)
      .build();

    newConnection
      .start()
      .then(() => {
        isConnectionRef.current = true;
        setConnection(newConnection);
      })
      .catch((err: Error) => console.error('Error while connecting SignalR: ', err));

    return () => {
      newConnection
        .stop()
        .then(() => {
          isConnectionRef.current = false;
          // setConnection(null);
          console.log('Connection stopped');
        })
        .catch((err) => console.error('Error while stopping connection: ', err));
    };
  }, [token]);

  //* If user lost socket connection and then reconnected
  //* Do general data requests again to keep data up to date
  useEffect(() => {
    if (connection) {
      connection.onreconnected(() => {
        //* Clear current chat state
        dispatch(
          setChatUsers({
            myChatUsers: [],
            notMyChatUsers: [],
            unassignedChatUsers: [],
            botsChatUsers: [],
            allChatUsers: [],
          }),
        );
        setChatUsersRequested({
          myChatUsers: false,
          notMyChatUsers: false,
          unassignedChatUsers: false,
          botsChatUsers: false,
          allChatUsers: false,
          customChatUsers: false,
        });
        dispatch(setChatUsersSavedMessages([]));

        //* Application data
        getDataOnLoad();
      });
    }
  }, [connection]);

  //* Online / offline indication
  useEffect(() => {
    let offlineNotif: any = null;
    const handleOnline = () => {
      if (offlineNotif !== null) closeSnackbar(offlineNotif);

      writePerformanceLog('ApplicationHealthLog', {
        action: 'connectionRecovered',
        date: new Date().toISOString(),
        // url: pageUrl,
      });
    };
    const handleOffline = () => {
      offlineNotif = enqueueSnackbar('Connection lost', {
        autoHideDuration: null,
        action: () => {
          return <></>;
        },
        variant: 'warning',
      });

      writePerformanceLog('ApplicationHealthLog', {
        action: 'connectionLost',
        date: new Date().toISOString(),
        // url: pageUrl,
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [closeSnackbar, enqueueSnackbar]);

  return <SignalRContext.Provider value={{ connection }}>{children}</SignalRContext.Provider>;
};

export { SignalRContext, SignalRProvider };
